import React from 'react';
import { format, isToday, isYesterday } from 'date-fns';

interface Thread {
  id: string;
  thread_id: string;
  subject?: string;
  snippet?: string;
  participants: {
    email: string;
    name?: string;
  }[];
  message_count: number;
  has_unread: boolean;
  last_message_at: string;
  is_starred?: boolean;
  sentiment?: 'positive' | 'neutral' | 'negative';
  sentiment_emoji?: string;
  intent?: string;
  intent_emoji?: string;
  priority_score?: number;
}

interface ThreadListProps {
  threads: Thread[];
  selectedThreadId: string | null;
  onSelectThread: (threadId: string) => void;
  selectedThreads?: Set<string>;
  onToggleThreadSelection?: (threadId: string) => void;
}

const ThreadList: React.FC<ThreadListProps> = ({
  threads,
  selectedThreadId,
  onSelectThread,
  selectedThreads = new Set(),
  onToggleThreadSelection
}) => {
  
  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    
    if (isToday(date)) {
      return format(date, 'h:mm a');
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else {
      return format(date, 'MMM d');
    }
  };
  
  // Helper to get displayable participants
  const getParticipantDisplay = (participants: Thread['participants']) => {
    if (!participants || participants.length === 0) {
      return 'Unknown';
    }
    
    const names = participants.map(p => p.name || p.email.split('@')[0]);
    
    if (names.length === 1) {
      return names[0];
    }
    
    return `${names[0]} + ${names.length - 1}`;
  };
  
  if (threads.length === 0) {
    return (
      <div className="p-4 text-center">
        <p className="text-gray-500">No conversations found</p>
      </div>
    );
  }
  
  return (
    <div className="w-full overflow-y-auto max-h-[calc(100vh-240px)]">
      {threads.map((thread) => (
        <div
          key={thread.id}
          className={`w-full py-3 px-4 cursor-pointer border-b border-gray-200 ${thread.thread_id === selectedThreadId ? 'bg-blue-50' : 'hover:bg-gray-100'} ${selectedThreads.has(thread.id) ? 'bg-blue-100' : ''}`}
        >
          <div className="flex items-start gap-3">
            {/* Checkbox for bulk selection */}
            {onToggleThreadSelection && (
              <input
                type="checkbox"
                checked={selectedThreads.has(thread.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleThreadSelection(thread.id);
                }}
                onClick={(e) => e.stopPropagation()}
                className="mt-1 cursor-pointer"
              />
            )}
            
            <div className="flex-1" onClick={() => onSelectThread(thread.thread_id)}>
              <div className="flex justify-between items-center mb-1">
                <p 
                  className={`${thread.has_unread ? 'font-bold' : 'font-normal'} flex-1 truncate`}
                >
                  {getParticipantDisplay(thread.participants)}
                </p>
                <p className="text-sm text-gray-500">
                  {formatDate(thread.last_message_at)}
                </p>
              </div>

          <div className="flex items-center mb-1 gap-2">
            <p 
              className={`${thread.has_unread ? 'font-bold' : 'font-normal'} text-base flex-1 truncate`}
            >
              {thread.subject || '(No subject)'}
            </p>
            
            {/* AI Badges */}
            <div className="flex items-center gap-1">
              {/* Sentiment Badge */}
              {thread.sentiment_emoji && (
                <span 
                  className="text-base" 
                  title={`Sentiment: ${thread.sentiment || 'unknown'}`}
                >
                  {thread.sentiment_emoji}
                </span>
              )}
              
              {/* Intent Badge */}
              {thread.intent_emoji && (
                <span 
                  className="text-base" 
                  title={`Intent: ${thread.intent || 'unknown'}`}
                >
                  {thread.intent_emoji}
                </span>
              )}
              
              {/* Priority Score */}
              {thread.priority_score && thread.priority_score >= 8 && (
                <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded">
                  HIGH
                </span>
              )}
            </div>
            
            {thread.has_unread && (
              <svg className="w-4 h-4 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 8v2a1 1 0 00.293.707l.707.707A6 6 0 0010 13h3.586l.707-.707a1 1 0 00.293-.707V10a1 1 0 00-.293-.707l-.707-.707A6 6 0 0013 2H10z" />
              </svg>
            )}
          </div>
          
              <div className="flex items-center">
                <p className="text-sm text-gray-500">
                  {thread.snippet || ''}
                </p>
                
                <div className="flex items-center ml-2">
                  {thread.is_starred && (
                    <svg className="w-4 h-4 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  )}
                  
                  {thread.message_count > 1 && (
                    <span className={`${thread.is_starred ? 'ml-2' : ''} bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full`}>
                      {thread.message_count}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ThreadList;
